import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Availability of the product page (`blocks/pc-pdp-availability.liquid`).
 *
 * The server renders the availability of the selected variant. When the visitor picks another size, the variant
 * picker fetches the page for that variant; this component takes the new availability text from that response, and
 * shows or hides the "Avisarme" disclosure depending on whether the new variant is sold out.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} status - Container of the availability text.
 * @property {HTMLDetailsElement} [notify] - "Avisarme" disclosure with the notification form.
 *
 * @extends {Component<Refs>}
 */
class PcAvailability extends Component {
  requiredRefs = ['status'];

  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
  }

  /**
   * Takes the availability of the newly selected variant from the section response.
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    const { promise } = /** @type {{ promise?: Promise<{ detail?: { html?: Document } }> }} */ (event);
    if (!promise) return;

    promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;

        const source = html.querySelector(`pc-availability[data-block-id="${this.dataset.blockId}"]`);
        const status = source?.querySelector('[ref="status"]');
        if (!status) return;

        // Read the state before moving the nodes: they leave the response document
        const soldOut = status.querySelector('[data-state]')?.getAttribute('data-state') === 'sold_out';

        const { status: current, notify } = this.refs;
        current.replaceChildren(...status.childNodes);

        // The disclosure is only for sold out variants (the theme editor always shows it)
        if (notify && this.dataset.designMode !== 'true') {
          notify.hidden = !soldOut;
          if (!soldOut) notify.open = false;
        }
      })
      .catch(() => {
        // Aborted or failed selection: keep the current text
      });
  };
}

if (!customElements.get('pc-availability')) {
  customElements.define('pc-availability', PcAvailability);
}
